import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield,
  Settings,
  Eye,
  Edit,
  Trash2,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Info
} from 'lucide-react';

interface Permission {
  id: string;
  resource: string;
  action: string;
  description: string;
  granted: boolean;
  scope?: string;
}

interface PermissionsReviewStepProps {
  onComplete: () => void;
  onSkip: () => void;
  onPrevious: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  isRequired: boolean;
  estimatedTime: string;
}

export const PermissionsReviewStep: React.FC<PermissionsReviewStepProps> = ({
  onComplete,
  onSkip,
  onPrevious,
  canGoBack,
  isRequired,
  estimatedTime
}) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      // Mock permissions based on typical user roles
      // In a real implementation, this would come from the backend
      const mockPermissions: Permission[] = [
        {
          id: 'recalls_read',
          resource: 'recalls',
          action: 'read',
          description: 'View recall information and details',
          granted: true,
          scope: 'organization'
        },
        {
          id: 'campaigns_read',
          resource: 'campaigns',
          action: 'read',
          description: 'View campaign information and assignments',
          granted: true,
          scope: 'organization'
        },
        {
          id: 'technicians_read',
          resource: 'technicians',
          action: 'read',
          description: 'View technician information and schedules',
          granted: true,
          scope: 'organization'
        },
        {
          id: 'dealers_read',
          resource: 'dealers',
          action: 'read',
          description: 'View dealer information and preferences',
          granted: true,
          scope: 'organization'
        },
        {
          id: 'assignments_update',
          resource: 'assignments',
          action: 'update',
          description: 'Update technician assignments and status',
          granted: true,
          scope: 'assigned_campaigns'
        },
        {
          id: 'reports_read',
          resource: 'reports',
          action: 'read',
          description: 'View reports and analytics',
          granted: true,
          scope: 'organization'
        },
        {
          id: 'users_manage',
          resource: 'users',
          action: 'manage',
          description: 'Manage user accounts and permissions',
          granted: false,
          scope: 'organization'
        },
        {
          id: 'system_admin',
          resource: 'system',
          action: 'admin',
          description: 'System administration and configuration',
          granted: false,
          scope: 'global'
        }
      ];

      setPermissions(mockPermissions);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'read':
        return <Eye className='h-4 w-4' />;
      case 'update':
      case 'edit':
        return <Edit className='h-4 w-4' />;
      case 'delete':
        return <Trash2 className='h-4 w-4' />;
      case 'manage':
        return <Settings className='h-4 w-4' />;
      case 'admin':
        return <Shield className='h-4 w-4' />;
      default:
        return <Settings className='h-4 w-4' />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'read':
        return 'text-blue-600';
      case 'update':
      case 'edit':
        return 'text-green-600';
      case 'delete':
        return 'text-red-600';
      case 'manage':
        return 'text-purple-600';
      case 'admin':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  const getScopeBadge = (scope?: string) => {
    if (!scope) return null;

    const scopeMap: Record<
      string,
      { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
    > = {
      organization: { label: 'Organization', variant: 'default' },
      assigned_campaigns: { label: 'Assigned Campaigns', variant: 'secondary' },
      global: { label: 'Global', variant: 'destructive' },
      personal: { label: 'Personal', variant: 'outline' }
    };

    const scopeInfo = scopeMap[scope] || { label: scope, variant: 'outline' as const };

    return (
      <Badge variant={scopeInfo.variant} className='text-xs'>
        {scopeInfo.label}
      </Badge>
    );
  };

  const grantedPermissions = permissions.filter(p => p.granted);
  const deniedPermissions = permissions.filter(p => !p.granted);

  if (loading) {
    return (
      <div className='space-y-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-200 rounded w-1/3 mb-4' />
          <div className='space-y-3'>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className='h-16 bg-gray-200 rounded' />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center space-y-2'>
        <div className='flex items-center justify-center gap-2'>
          <Shield className='h-6 w-6 text-primary' />
          <h3 className='text-xl font-semibold'>Permissions Review</h3>
        </div>
        <p className='text-muted-foreground'>
          Review your access levels and permissions in the system
        </p>
        <Badge variant='outline'>{estimatedTime}</Badge>
      </div>

      {/* Permissions Overview */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <Card>
          <CardContent className='p-4 text-center'>
            <div className='text-2xl font-bold text-green-600'>{grantedPermissions.length}</div>
            <div className='text-sm text-muted-foreground'>Granted Permissions</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4 text-center'>
            <div className='text-2xl font-bold text-red-600'>{deniedPermissions.length}</div>
            <div className='text-sm text-muted-foreground'>Restricted Access</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4 text-center'>
            <div className='text-2xl font-bold text-blue-600'>{permissions.length}</div>
            <div className='text-sm text-muted-foreground'>Total Permissions</div>
          </CardContent>
        </Card>
      </div>

      {/* Granted Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-lg'>
            <CheckCircle className='h-5 w-5 text-green-600' />
            Your Permissions
          </CardTitle>
          <CardDescription>These are the actions you can perform in the system</CardDescription>
        </CardHeader>
        <CardContent className='space-y-3'>
          {grantedPermissions.map(permission => (
            <div
              key={permission.id}
              className='flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg'
            >
              <div className='flex items-center gap-3'>
                <div className={`${getActionColor(permission.action)}`}>
                  {getActionIcon(permission.action)}
                </div>
                <div>
                  <div className='font-medium capitalize'>
                    {permission.action} {permission.resource}
                  </div>
                  <div className='text-sm text-muted-foreground'>{permission.description}</div>
                </div>
              </div>
              <div className='flex items-center gap-2'>
                {getScopeBadge(permission.scope)}
                <Badge variant='default' className='bg-green-600'>
                  Granted
                </Badge>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Restricted Permissions */}
      {deniedPermissions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2 text-lg'>
              <AlertTriangle className='h-5 w-5 text-orange-600' />
              Restricted Access
            </CardTitle>
            <CardDescription>These permissions are not available to your role</CardDescription>
          </CardHeader>
          <CardContent className='space-y-3'>
            {deniedPermissions.map(permission => (
              <div
                key={permission.id}
                className='flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg'
              >
                <div className='flex items-center gap-3'>
                  <div className={`${getActionColor(permission.action)} opacity-50`}>
                    {getActionIcon(permission.action)}
                  </div>
                  <div>
                    <div className='font-medium capitalize text-muted-foreground'>
                      {permission.action} {permission.resource}
                    </div>
                    <div className='text-sm text-muted-foreground'>{permission.description}</div>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  {getScopeBadge(permission.scope)}
                  <Badge variant='outline' className='border-orange-300 text-orange-600'>
                    Restricted
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Information Notice */}
      <div className='flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
        <Info className='h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0' />
        <div className='text-sm'>
          <p className='font-medium text-blue-900 mb-1'>About Permissions</p>
          <p className='text-blue-700'>
            Your permissions are based on your role and organizational access level. If you need
            additional permissions, please contact your manager or system administrator.
          </p>
        </div>
      </div>

      {/* Acknowledgment */}
      <div className='flex items-center gap-3 p-4 bg-muted/50 rounded-lg'>
        <Checkbox
          id='acknowledge'
          checked={acknowledged}
          onCheckedChange={(checked: boolean) => setAcknowledged(checked)}
        />
        <label htmlFor='acknowledge' className='text-sm'>
          I have reviewed my permissions and understand my access levels in the system.
        </label>
      </div>

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-6 border-t'>
        <div className='flex gap-2'>
          {canGoBack && (
            <Button variant='outline' onClick={onPrevious}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Previous
            </Button>
          )}
        </div>

        <div className='flex gap-2'>
          {!isRequired && (
            <Button variant='ghost' onClick={onSkip}>
              Skip for now
            </Button>
          )}
          <Button onClick={onComplete} disabled={!acknowledged}>
            Continue to Training
            <ArrowRight className='h-4 w-4 ml-2' />
          </Button>
        </div>
      </div>
    </div>
  );
};
